import freeEmailDomains from 'free-email-domains'
import { z } from 'zod'
import { companySchema, companyUserSchema } from '../schemas/companies.schemas'
import {
  CompanyType,
  CompanyUsersRelation,
  CompanyUsersStatus
} from '../types/companies.types'
import { DefaultRole } from '../types/roles.types'

const updateableCompanySchema = companySchema.pick({
  name: true,
  tin: true,
  type: true,
  logoUri: true,
  address: true,
  state: true,
  country: true,
  zipcode: true,
  webUrl: true
})

export const createCompanyBodySchema = z.object({
  company: updateableCompanySchema,
  users: z
    .array(
      z.object({
        email: z.email(),
        role: z.enum(DefaultRole),
        relation: z.enum(CompanyUsersRelation)
      })
    )
    .optional()
})
export type CreateCompanyBody = z.infer<typeof createCompanyBodySchema>

export const companiesParamsSchema = z.object({
  companyId: z.uuid()
})

export const companiesUsersParamsSchema = companiesParamsSchema.extend({
  userId: z.uuid()
})

export const updateCompanyBodySchema = updateableCompanySchema
  .partial()
  .refine(data => Object.keys(data).length > 0, {
    message: 'At least one field must be provided'
  })

export type UpdateCompanyBody = z.infer<typeof updateCompanyBodySchema>

export const createCompanyUserBodySchema = companyUserSchema
  .pick({
    // roleId: true Not posible to use because it has to be mandatory, not optional as the schema
    // relation: true, Not posible to use because it has to be mandatory, not optional as the schema
    jobTitle: true
  })
  .extend({
    roleId: z.uuid(),
    relation: z.enum(CompanyUsersRelation),
    email: z
      .email('email.invalid_format')
      .refine(
        email => !freeEmailDomains.includes(email.split('@')[1]),
        'common.email.invalid_domain'
      ),
    workspaces: z.array(z.string()).optional()
  })

export type CreateCompanyUserBody = z.infer<typeof createCompanyUserBodySchema>

export const updateCompanyUserBodySchema = companyUserSchema
  .pick({
    roleId: true,
    relation: true,
    jobTitle: true
  })
  .partial()
  .refine(data => Object.keys(data).length > 0, {
    message: 'At least one field must be provided'
  })

export type UpdateCompanyUserBody = z.infer<typeof updateCompanyUserBodySchema>

export const getCompaniesQuerySchema = z.object({
  associated: z.string().optional()
})

export const updateCompanyFormSchema = z
  .object({
    name: z.string().min(1, { message: 'common.company_name_required' }),
    tin: z.string().min(1, { message: 'common.company_tin_required' }),
    type: z.enum(CompanyType),
    address: z.string().optional(),
    zipcode: z
      .string()
      .optional()
      .refine(
        val =>
          val === undefined ||
          val === '' ||
          (/^\d{5}$/.test(val) && +val >= 1000 && +val <= 52999),
        {
          message: 'common.invalid_zip_code'
        }
      ),
    country: z.string().optional(),
    webUrl: z
      .string()
      .optional()
      .refine(
        val =>
          val === undefined || val === '' || z.url().safeParse(val).success,
        {
          message: 'common.invalid_web_url'
        }
      ),
    state: z.string().optional(),
    logoUri: z.string().optional().nullable()
  })
  .strict()
export type UpdateCompanyForm = z.infer<typeof updateCompanyFormSchema>

export const listCompanyUsersQuerySchema = z.object({
  status: z.enum(CompanyUsersStatus).optional(),
  relation: z.enum(CompanyUsersRelation).optional(),
  role: z.string().optional()
})

export const acceptCompanyUserSchema = z.object({
  email: z.email(),
  roleId: z.uuid(),
  relation: z.enum(CompanyUsersRelation),
  workspaces: z.array(z.string()).optional(),
  jobTitle: z.string().optional()
})
export type AcceptCompanyUser = z.infer<typeof acceptCompanyUserSchema>

export const companyMemberEmailSchema = z
  .union([z.literal(''), z.email()])
  .refine(
    email => !freeEmailDomains.includes(email.split('@')[1]),
    'Invalid email domain'
  )

export const createCompanyFormSchema = z.object({
  company: z.object({
    name: z.string().min(1, { message: 'common.field_required' }),
    tin: z.string().min(1, { message: 'common.field_required' }),
    type: z.enum(CompanyType),
    logoUri: z.string().optional().nullable()
  }),
  users: z
    .array(
      z.object({
        email: companyMemberEmailSchema,
        role: z.enum(DefaultRole).or(z.literal('')),
        relation: z.enum(CompanyUsersRelation).or(z.literal(''))
      })
    )
    .optional()
})
export type CreateCompanyForm = z.infer<typeof createCompanyFormSchema>
